import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import {
  Box,
  Card,
  Stack,
  Table,
  Button,
  Divider,
  Tooltip,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  TableHead,
  TextField,
  Typography,
  LinearProgress,
  TableContainer,
} from '@mui/material';
// components
import Label from 'src/components/Label';
import Iconify from 'src/components/Iconify';
// utils
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';

// ---------------------------------------------------------------------------
// Recorded-video progress for ONE track of a diploma. Rows are the diploma's
// enrolled students, columns are the track's videos; each cell is how much of
// the video the student ACTUALLY watched (real watch-time, not "reached the
// end") — a video counts complete at >= the threshold the backend applies
// (80%). A batch filter narrows the roster; Export CSV downloads the same grid.
// ---------------------------------------------------------------------------

type Video = {
  itemId: string;
  title: string;
  courseTitle: string;
  durationMinutes: number;
};
type Cell = { pct: number; completed: boolean; watchedSeconds: number };
type Student = {
  _id: string;
  name: string;
  email: string;
  cells: Record<string, Cell>;
  completedCount: number;
  totalVideos: number;
  completedPct: number;
  avgWatchedPct: number;
};
type Matrix = {
  threshold: number;
  totalVideos: number;
  videos: Video[];
  students: Student[];
};

type TrackOpt = { _id: string; title: string };
type Props = { diplomaId: string; batchNames: string[] };

export default function RecordedProgressCard({ diplomaId, batchNames }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [trackOpts, setTrackOpts] = useState<TrackOpt[]>([]);
  const [trackId, setTrackId] = useState('');
  const [batch, setBatch] = useState('');
  const [matrix, setMatrix] = useState<Matrix | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Load this diploma's assigned tracks (the export is per-track).
  useEffect(() => {
    if (!diplomaId) return;
    let alive = true;
    axiosInstance
      .get(APIS.DIPLOMAS.BY_ID(diplomaId))
      .then((res) => {
        if (!alive) return;
        const d = res.data?.data ?? res.data;
        const opts: TrackOpt[] = (d?.tracks || []).map((t: any) => ({
          _id: String(t?._id ?? t),
          title: t?.title || t?.title_ar || 'Track',
        }));
        setTrackOpts(opts);
        if (opts.length && !trackId) setTrackId(opts[0]._id);
      })
      .catch(() => setTrackOpts([]));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diplomaId]);

  // Load the matrix whenever the track / batch changes.
  useEffect(() => {
    if (!diplomaId || !trackId) {
      setMatrix(null);
      return;
    }
    let alive = true;
    setLoading(true);
    axiosInstance
      .get(APIS.DIPLOMAS.RECORDED_MATRIX(diplomaId, trackId, batch))
      .then((res) => {
        if (!alive) return;
        setMatrix((res.data?.data ?? res.data) as Matrix);
      })
      .catch((e: any) => {
        if (!alive) return;
        setMatrix(null);
        enqueueSnackbar(e?.message || 'Failed to load recorded progress', {
          variant: 'error',
        });
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [diplomaId, trackId, batch, enqueueSnackbar]);

  // Export via axiosInstance (blob) so the Bearer token is sent.
  const handleExport = async () => {
    if (!trackId) return;
    setExporting(true);
    try {
      const res = await axiosInstance.get(
        APIS.DIPLOMAS.RECORDED_EXPORT_CSV(diplomaId, trackId, batch),
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `recorded-progress-${batch || 'diploma'}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Export failed', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const threshold = matrix?.threshold ?? 80;

  return (
    <Card sx={{ mt: 3 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ p: 2.5 }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6">Recorded-video progress</Typography>
          <Typography variant="body2" color="text.secondary">
            Real watch-time per student. A video is complete at ≥ {threshold}%
            actually watched — seeking to the end does not count.
          </Typography>
        </Box>

        <TextField
          select
          size="small"
          label="Track"
          value={trackId}
          onChange={(e) => setTrackId(e.target.value)}
          sx={{ minWidth: 200 }}
          helperText={trackOpts.length ? '' : 'Assign a track to this diploma first.'}
        >
          {trackOpts.map((t) => (
            <MenuItem key={t._id} value={t._id}>
              {t.title}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Batch"
          value={batch}
          onChange={(e) => setBatch(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All batches</MenuItem>
          {batchNames.filter(Boolean).map((b) => (
            <MenuItem key={b} value={b}>
              {b}
            </MenuItem>
          ))}
        </TextField>

        <Button
          variant="contained"
          startIcon={<Iconify icon="eva:download-outline" />}
          onClick={handleExport}
          disabled={!trackId || exporting || loading}
        >
          {exporting ? 'Exporting…' : 'Export CSV'}
        </Button>
      </Stack>
      <Divider />

      <TableContainer sx={{ position: 'relative', overflowX: 'auto' }}>
        {loading && (
          <LinearProgress
            sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 9 }}
          />
        )}
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 2 }}>
                Student
              </TableCell>
              {(matrix?.videos || []).map((v, i) => (
                <TableCell key={v.itemId} align="center">
                  <Tooltip title={`${v.courseTitle} — ${v.title}`}>
                    <span>V{i + 1}</span>
                  </Tooltip>
                </TableCell>
              ))}
              <TableCell align="center">Completed</TableCell>
              <TableCell align="center">Completion %</TableCell>
              <TableCell align="center">Avg watched %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && (!matrix || matrix.students.length === 0) && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                  {trackId
                    ? 'No enrolled students / videos for this selection.'
                    : 'Pick a track to see recorded-video progress.'}
                </TableCell>
              </TableRow>
            )}
            {(matrix?.students || []).map((s) => (
              <TableRow key={s._id} hover>
                <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                  <Typography variant="subtitle2" noWrap>
                    {s.name || '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {s.email}
                  </Typography>
                </TableCell>
                {(matrix?.videos || []).map((v) => {
                  const c = s.cells[v.itemId];
                  const pct = c?.pct ?? 0;
                  return (
                    <TableCell key={v.itemId} align="center">
                      <Label color={c?.completed ? 'success' : pct > 0 ? 'warning' : 'error'}>
                        {pct}%
                      </Label>
                    </TableCell>
                  );
                })}
                <TableCell align="center">
                  {s.completedCount}/{s.totalVideos}
                </TableCell>
                <TableCell align="center">
                  <Label color={s.completedPct >= threshold ? 'success' : 'default'}>
                    {s.completedPct}%
                  </Label>
                </TableCell>
                <TableCell align="center">{s.avgWatchedPct}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}
